import { useState, useEffect } from 'react';
import { X, Check, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useWorkflows, usePreviewWorkflowChange, useMigrateProjectWorkflow } from '../../hooks/useWorkflows';
import { getContrastText } from '../issue/IssueListView';
import type { WorkflowPreviewResult } from '../../services/workflowService';
import SingleSelectDropdown from '../common/SingleSelectDropdown';

interface Props {
  projectId: string;
  currentWorkflowId: string;
  currentWorkflowName: string;
  onClose: () => void;
  onMigrated: () => void;
}

export default function WorkflowMigrationModal({
  projectId,
  currentWorkflowId,
  currentWorkflowName,
  onClose,
  onMigrated,
}: Props) {
  const { data: workflows } = useWorkflows();
  const previewMutation = usePreviewWorkflowChange();
  const migrateMutation = useMigrateProjectWorkflow();

  const [targetWorkflowId, setTargetWorkflowId] = useState('');
  const [preview, setPreview] = useState<WorkflowPreviewResult | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});

  const availableWorkflows = workflows?.filter((w) => w.workflowId !== currentWorkflowId) ?? [];

  // Fetch preview when target changes
  useEffect(() => {
    if (!targetWorkflowId) {
      setPreview(null);
      setMapping({});
      return;
    }

    previewMutation.mutate(
      { projectId, targetWorkflowId },
      {
        onSuccess: (data) => {
          setPreview(data);
          // Apply suggested mappings
          const init: Record<string, string> = {};
          for (const suggestion of data.suggestedMapping) {
            init[suggestion.fromStatusId] = suggestion.toStatusId;
          }
          setMapping(init);
        },
        onError: () => {
          toast.error('미리보기 조회에 실패했습니다.');
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetWorkflowId, projectId]);

  const statusesRequiringMapping = preview?.statusDistribution.filter((s) => s.issueCount > 0) ?? [];
  const allMapped = statusesRequiringMapping.every((s) => mapping[s.statusId]);
  const suggestedIds = new Set(preview?.suggestedMapping.map((s) => s.fromStatusId) ?? []);

  const handleMigrate = async () => {
    if (!preview || !targetWorkflowId) return;

    const statusMapping = Object.entries(mapping)
      .filter(([, toId]) => toId)
      .map(([fromId, toId]) => ({ fromStatusId: fromId, toStatusId: toId }));

    try {
      const result = await migrateMutation.mutateAsync({
        projectId,
        data: { targetWorkflowId, statusMapping },
      });
      toast.success(`워크플로우 변경 완료 — ${result.migratedIssueCount}건의 이슈가 마이그레이션되었습니다.`);
      onMigrated();
    } catch {
      toast.error('워크플로우 변경에 실패했습니다.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto py-8">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-base font-semibold text-gray-900">워크플로우 변경</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="size-5" />
          </button>
        </div>

        <div className="p-4 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Current workflow */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">현재: {currentWorkflowName}</p>
            {preview && (
              <div className="flex items-center gap-1 flex-wrap">
                {preview.statusDistribution.map((s, i) => (
                  <span key={s.statusId} className="flex items-center gap-1">
                    <span className="text-sm text-gray-600">
                      {s.name} ({s.issueCount}건)
                    </span>
                    {i < preview.statusDistribution.length - 1 && (
                      <span className="text-gray-300 text-sm">→</span>
                    )}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Target workflow selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">변경할 워크플로우</label>
            <SingleSelectDropdown
              label="선택하세요"
              value={targetWorkflowId}
              onChange={setTargetWorkflowId}
              options={availableWorkflows.map((w) => ({
                value: w.workflowId,
                label: `${w.name}${w.isDefault ? ' (기본)' : ''}`,
              }))}
            />

            {preview && (
              <div className="flex items-center gap-1 flex-wrap mt-2">
                {preview.targetWorkflow.statuses.map((s, i) => (
                  <span key={s.statusId} className="flex items-center gap-1">
                    <span
                      className="inline-flex items-center px-2 py-0.5 text-sm font-medium rounded-full"
                      style={{ backgroundColor: s.color, color: getContrastText(s.color) }}
                    >
                      {s.name}
                    </span>
                    {i < preview.targetWorkflow.statuses.length - 1 && (
                      <span className="text-gray-300 text-sm">→</span>
                    )}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Mapping Table */}
          {preview && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3">상태 매핑</h4>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Table header */}
                <div className="grid grid-cols-[1fr_80px_30px_1fr_100px] gap-2 px-3 py-2 bg-gray-50 text-sm font-medium text-gray-500">
                  <span>현재 상태</span>
                  <span className="text-center">이슈 수</span>
                  <span />
                  <span>새 상태</span>
                  <span />
                </div>

                {/* Table rows */}
                {preview.statusDistribution.map((status) => (
                  <div
                    key={status.statusId}
                    className="grid grid-cols-[1fr_80px_30px_1fr_100px] gap-2 px-3 py-2 border-t border-gray-100 items-center"
                  >
                    <span className="text-sm text-gray-700">{status.name}</span>
                    <span className="text-sm text-gray-500 text-center">
                      {status.issueCount > 0 ? `${status.issueCount}건` : '-'}
                    </span>
                    <span className="text-gray-400 text-center">→</span>

                    {status.issueCount > 0 ? (
                      <SingleSelectDropdown
                        label="선택하세요"
                        value={mapping[status.statusId] || ''}
                        onChange={(v) =>
                          setMapping((prev) => ({ ...prev, [status.statusId]: v }))
                        }
                        options={preview.targetWorkflow.statuses.map((ts) => ({
                          value: ts.statusId,
                          label: ts.name,
                        }))}
                      />
                    ) : (
                      <span className="text-sm text-gray-400 italic">매핑 불필요</span>
                    )}

                    <span className="text-sm">
                      {status.issueCount > 0 && mapping[status.statusId] ? (
                        <span className="flex items-center gap-0.5 text-green-600">
                          <Check className="size-3" />
                          {suggestedIds.has(status.statusId) ? '자동매핑' : '선택됨'}
                        </span>
                      ) : status.issueCount > 0 ? (
                        <span className="flex items-center gap-0.5 text-amber-600">
                          <AlertTriangle className="size-3" />
                          선택 필요
                        </span>
                      ) : null}
                    </span>
                  </div>
                ))}
              </div>

              {statusesRequiringMapping.length > 0 && (
                <p className="text-sm text-gray-500 mt-2">
                  ※ 이슈가 있는 모든 상태의 매핑이 완료되어야 변경할 수 있습니다
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            취소
          </button>
          <button
            onClick={handleMigrate}
            disabled={!targetWorkflowId || !allMapped || migrateMutation.isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {migrateMutation.isPending ? '변경 중...' : '워크플로우 변경'}
          </button>
        </div>
      </div>
    </div>
  );
}
